import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "./config";
import { DEFAULT_ABOUT } from "./constants";
import InlineBanner from "./InlineBanner";
import { MapSkeleton } from "./skeletons";
import { asset } from "./assetUrl";
import EmptyState from "./EmptyState";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

/**
 * Group members map — Skool-style member discovery.
 * Fetches `membership-members-map` (privacy already masked server-side) and
 * renders each member as an avatar marker, with name search, region filters,
 * a "near me" toggle, and an admin distribution panel.
 */

// Map chrome. Leaflet ships its own look (blue links, square controls, sharp
// popups); this is the house style on top of it — calmer tiles so the avatars
// are what the eye lands on, rounded controls, and a card-like popup.
const MAP_STYLES = `
.ff-map .leaflet-tile { filter: saturate(0.78) contrast(1.03) brightness(1.03); }
.ff-map .leaflet-container { background: #eef2f6; }
.ff-map .ff-avatar-pin { transition: transform .18s ease; }
.ff-map .ff-avatar-pin:hover { transform: translateY(-2px) scale(1.06); z-index: 1000; }
.ff-map .leaflet-bar { border: none; box-shadow: 0 6px 18px -8px rgba(16,24,40,.35); border-radius: 12px; overflow: hidden; }
.ff-map .leaflet-bar a { width: 32px; height: 32px; line-height: 32px; color: #334155; border-bottom-color: #eef1f4; font-size: 17px; }
.ff-map .leaflet-bar a:hover { background: #f8fafc; }
.ff-map .leaflet-popup-content-wrapper { border-radius: 16px; box-shadow: 0 18px 40px -18px rgba(16,24,40,.45); padding: 4px; }
.ff-map .leaflet-popup-content { margin: 12px 14px; }
.ff-map .leaflet-popup-tip { box-shadow: none; }
`;

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

/** "1 Instructor", "4 Members" — drop the plural when there is only one. */
const plural = (n, word) => (n === 1 ? word : `${word}s`);

/** Everyone who runs the membership: admins AND instructors. */
const isStaff = (m) => !!(m.is_admin || m.is_instructor);

/** The one-word role shown on the pin badge, the popup and the profile card. */
const roleOf = (m) => (m.is_admin ? "Admin" : m.is_instructor ? "Instructor" : "Member");

const ROLE_TINT = { Admin: "#F0A500", Instructor: "#5A86E6", Member: null };

// Teardrop pin with the member's avatar in the head. Staff get their role's
// accent ring plus a small badge, so who runs the place reads at a glance
// without opening anything.
function avatarIcon(m, primary, { active = false } = {}) {
  const initial = escapeHtml((m.name || "?").charAt(0).toUpperCase());
  const inner = m.img
    ? `<img src="${escapeHtml(m.img)}" alt="" style="width:100%;height:100%;object-fit:cover" onerror="this.parentNode.innerHTML='<span style=\\'color:#fff;font-weight:800;font-size:15px\\'>${initial}</span>'"/>`
    : `<span style="color:#fff;font-weight:800;font-size:15px">${initial}</span>`;

  const role = roleOf(m);
  const accent = ROLE_TINT[role] || primary;
  const size = active ? 50 : 44;
  const badge = role === "Member"
    ? ""
    : `<span style="position:absolute;right:-2px;top:-2px;width:16px;height:16px;border-radius:50%;` +
      `background:${accent};border:2px solid rgb(var(--c-surface));display:flex;align-items:center;justify-content:center;` +
      `box-shadow:0 1px 3px rgba(16,24,40,.3)">` +
      `<svg viewBox="0 0 24 24" width="9" height="9" fill="#fff">${
        role === "Admin"
          ? '<path d="M5 20h14v-2H5v2zM12 3l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18.8 5.8 22 7 15.2 2 10.3l6.9-1L12 3z"/>'
          : '<path d="M12 3L1 9l11 6 9-4.9V17h2V9L12 3zM5 13.2V17c0 1.7 3.1 3 7 3s7-1.3 7-3v-3.8l-7 3.8-7-3.8z"/>'
      }</svg></span>`;

  return L.divIcon({
    className: "ff-avatar-pin",
    html:
      `<div style="position:relative;width:${size}px;height:${size + 10}px;` +
      `filter:drop-shadow(0 6px 10px rgba(16,24,40,.28));transition:transform .18s ease;` +
      `transform:${active ? "scale(1.04)" : "none"}">` +
        // The stem, drawn behind the head so the avatar sits on a point.
        `<span style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:0;height:0;` +
        `border-left:7px solid transparent;border-right:7px solid transparent;border-top:11px solid ${accent}"></span>` +
        `<span style="position:absolute;inset:0 0 10px 0;border-radius:50%;padding:3px;background:${accent};` +
        `display:block">` +
          `<span style="display:flex;width:100%;height:100%;border-radius:50%;overflow:hidden;` +
          `background:${primary};align-items:center;justify-content:center;box-shadow:inset 0 0 0 2px rgb(var(--c-surface))">${inner}</span>` +
        `</span>` +
        badge +
      `</div>`,
    iconSize: [size, size + 10],
    iconAnchor: [size / 2, size + 10],
    popupAnchor: [0, -(size + 4)],
  });
}

/**
 * Spread pins that share a coordinate.
 *
 * Locations are masked to a ~1 km grid (or a country centroid), so every member
 * in the same city lands on the EXACT same point and only the last one drawn is
 * clickable — a map of 40 members would show 6 pins. Members sharing a spot are
 * fanned onto a small ring around it, deterministically (index-based, no
 * randomness) so the layout is stable between renders.
 */
function spreadOverlaps(members) {
  const groups = new Map();
  members.forEach((m) => {
    const key = `${Number(m.lat).toFixed(3)},${Number(m.lng).toFixed(3)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  });

  const out = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      out.push({ ...group[0], _lat: group[0].lat, _lng: group[0].lng, _stack: 1 });
      return;
    }
    // Rings of 8, each ~1.5 km further out, so a busy city stays readable.
    group.forEach((m, i) => {
      const ring = Math.floor(i / 8) + 1;
      const step = (2 * Math.PI) / Math.min(group.length - Math.floor(i / 8) * 8, 8);
      const angle = step * (i % 8);
      const radius = 0.013 * ring;
      const latRad = (Number(m.lat) * Math.PI) / 180;
      out.push({
        ...m,
        _lat: Number(m.lat) + radius * Math.sin(angle),
        _lng: Number(m.lng) + (radius * Math.cos(angle)) / Math.max(0.2, Math.cos(latRad)),
        _stack: group.length,
      });
    });
  });

  return out;
}

// Fit the map to the visible markers (or recenter on a focused member).
function FitTo({ points, focus }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 80);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (focus) {
      map.setView([focus.lat, focus.lng], Math.max(map.getZoom(), 9), { animate: true });
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 9, { animate: false });
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, focus, map]);
  return null;
}

function SmallAvatar({ name, img, primary, size = 44, ring }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="rounded-full overflow-hidden flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, backgroundColor: primary, fontSize: size * 0.4, boxShadow: ring ? `0 0 0 2px ${ring}` : undefined }}
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : initial}
    </span>
  );
}

/**
 * Premium filter dropdown — a styled trigger (rounded pill + chevron, brand-tinted
 * when a value is chosen) opening a floating panel with hover / selected states and
 * a check mark, instead of the browser's native <select> chrome. `options` accepts
 * plain strings (value === label) or {value, label} pairs.
 */
function Select({ value, onChange, options, placeholder, primary }) {
  const [open, setOpen] = useState(false);
  // Where to draw the menu, in viewport coordinates.
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // The menu is PORTALLED to <body> rather than drawn inside this button's
  // container: the filter row lives in a rounded card with overflow-hidden, so
  // an absolutely-positioned menu was clipped at the card's edge — the longest
  // option ("Admins & instructors") was cut in half and the list ran under the
  // right-hand rail.
  const place = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 190);
    const margin = 8;
    // Prefer left-aligned with the trigger; flip to right-aligned when that
    // would run past the viewport.
    const left = Math.min(Math.max(margin, r.left), window.innerWidth - width - margin);
    setPos({ top: r.bottom + 6, left, width });
  };

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDoc = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    // Anything that moves the trigger moves the menu with it.
    const onMove = () => place();
    addOutsidePress(onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      removeOutsidePress(onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  const items = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const active = value !== "" && value != null;
  const current = items.find((i) => i.value === value);
  const label = current ? current.label : placeholder;

  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  const Check = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  const row = (v, text, on) => (
    <button
      key={v || "__all"}
      type="button"
      onClick={() => pick(v)}
      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition hover:bg-gray-50"
      style={on ? { backgroundColor: `${primary}12`, color: primary, fontWeight: 600 } : { color: "rgb(var(--c-gray-700))" }}
    >
      <span className="flex-1 truncate">{text}</span>
      {on && <Check />}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border bg-surface pl-3.5 pr-2.5 py-2 text-sm font-medium shadow-sm outline-none transition hover:shadow"
        style={{
          minWidth: 130,
          borderColor: active ? primary : open ? `${primary}80` : "rgb(var(--c-gray-200))",
          color: active ? primary : "rgb(var(--c-gray-700))",
          boxShadow: open ? `0 0 0 3px ${primary}1f` : undefined,
        }}
      >
        <span className="flex-1 truncate text-left">{label}</span>
        <svg
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          style={{ color: active ? primary : "rgb(var(--c-gray-400))" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="chat-pop fixed z-[2000] overflow-hidden rounded-2xl border border-gray-100 bg-surface p-1.5 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.35)]"
          style={{ top: pos.top, left: pos.left, minWidth: pos.width, maxWidth: 280 }}
        >
          <div className="thin-scroll max-h-64 overflow-y-auto">
            {row("", placeholder, !active)}
            {items.length > 0 && <div className="my-1 h-px bg-gray-100" />}
            {items.map((it) => row(it.value, it.label, it.value === value))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function MapView({ token, groupToken, primary = "#0274F7", member, groupCard, pointsCard, topBanner }) {
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState(null);
  // Membership-first: the map always plots everyone in the membership (the
  // group-scope filter, inherited from the group-based reference app, is gone).
  const [q, setQ] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");
  const [fRole, setFRole] = useState("");
  const [near, setNear] = useState(false);
  const [radius] = useState(100);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-members-map`, {
        community_token: token,
        member_token: member?.auth_token,
        community_member_id: member?.community_member_id,
      })
      .then((res) => {
        if (active && res.data?.error === 0) setResp(res.data);
        else if (active) setResp({ members: [], facets: {} });
      })
      .catch(() => active && setResp({ members: [], facets: {} }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, member]);

  const all = useMemo(() => resp?.members || [], [resp]);
  const facets = resp?.facets || { countries: [], states: [], cities: [] };
  const me = resp?.me || null;
  const isAdmin = !!resp?.is_admin;
  const distribution = resp?.distribution || null;
  // Members with no coordinates are not mappable; the API leaves them out and
  // reports how many so the header can account for them.
  const withoutLocation = Number(resp?.without_location || 0);
  // Set by the API when its pin cap clipped the result. `mapped` is what we were
  // actually given, `mappableTotal` how many exist — the pair is only rendered
  // when they differ. Both fall back so an older API build (which sends neither)
  // simply never trips the warning rather than printing "of 0".
  const truncated = !!resp?.truncated;
  const mapped = all.length;
  const mappableTotal = Number(resp?.mappable_total ?? all.length);

  // All filtering is client-side over the privacy-masked set returned once.
  // Everything EXCEPT the role filter. The legend counts come from here, so
  // picking "Instructor" doesn't zero the Admin and Member chips — they keep
  // showing how many are available to switch to.
  const preRole = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((m) => {
      if (needle && !(m.name || "").toLowerCase().includes(needle)) return false;
      if (fCountry && m.country !== fCountry) return false;
      if (fState && m.state !== fState) return false;
      if (fCity && m.city !== fCity) return false;
      if (near && (m.distance_km == null || m.distance_km > radius)) return false;
      return true;
    });
  }, [all, q, fCountry, fState, fCity, near, radius]);

  const filtered = useMemo(() => {
    // One option per role, each showing exactly that role — the same three the
    // legend counts. is_admin used to be the only flag the API set, and it set
    // it for instructors on the all-members map, so "Admins" listed the
    // instructors and hid the admins.
    let list = preRole.filter((m) => {
      if (fRole === "admin" && !m.is_admin) return false;
      if (fRole === "instructor" && !m.is_instructor) return false;
      if (fRole === "member" && isStaff(m)) return false;
      return true;
    });
    if (near) list = [...list].sort((a, b) => (a.distance_km ?? 1e9) - (b.distance_km ?? 1e9));
    return list;
  }, [preRole, fRole, near]);

  // Pins to draw: the filtered set with same-spot members fanned apart.
  const plotted = useMemo(() => spreadOverlaps(filtered), [filtered]);

  // Legend counts — how many of each role the OTHER filters leave available.
  const roleCounts = useMemo(() => {
    let admins = 0;
    let instructors = 0;
    preRole.forEach((m) => {
      if (m.is_admin) admins += 1;
      else if (m.is_instructor) instructors += 1;
    });

    return { admins, instructors, members: preRole.length - admins - instructors };
  }, [preRole]);

  const hasMyLocation = !!me;

  const openDetail = (m) => setDetail(m);

  const resetFilters = () => {
    setQ(""); setFCountry(""); setFState(""); setFCity(""); setFRole(""); setNear(false);
  };

  const activeFilters = q || fCountry || fState || fCity || fRole || near;

  const mapBody = (
    <div className="space-y-5">
      {/* Premium, map-focused card: header strip + big full-width map. */}
      <div className="rounded-3xl border border-gray-200 shadow-sm overflow-hidden bg-surface">
        {/* Header strip — light theme tinted with the community colour, matching
            the other member-page section headers. */}
        <div
          className="p-4 sm:p-5"
          style={{
            background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
            borderBottom: `1px solid ${primary}1f`,
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border bg-surface shadow-sm"
                style={{ color: primary, borderColor: `${primary}29` }}
              >
                <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </span>
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight text-gray-900 leading-tight">Member Map</h2>
                <p className="text-[13px] text-gray-500 mt-0.5 truncate">Tap any avatar to view a member's profile</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap" style={{ backgroundColor: `${primary}14`, color: primary }}>
                {loading ? "…" : `${filtered.length} on the map`}
              </span>
              {/* The map can only show members who shared a location, so the
                  count above is smaller than the membership. Say why rather than
                  leaving the difference unexplained. */}
              {!loading && withoutLocation > 0 && (
                <span className="text-[11px] text-gray-400 whitespace-nowrap">
                  {withoutLocation} {withoutLocation === 1 ? "member hasn't" : "members haven't"} shared a location
                </span>
              )}
              {/* The API caps how many pins it will return (MAP_MAX_PINS). Below
                  that cap this renders nothing, which is the normal case — it
                  exists so a clipped map can never be read as a small community,
                  the way the old flat 500-row limit silently was. Amber, not
                  grey: unlike the line above, this one means data is missing. */}
              {!loading && truncated && (
                <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-300 whitespace-nowrap">
                  Showing {mapped.toLocaleString()} of {mappableTotal.toLocaleString()} members with a location
                </span>
              )}
            </div>
          </div>

          {/* Search + filters */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search members by name…"
                className="w-full rounded-full border border-gray-200 bg-surface pl-10 pr-3 py-2.5 text-sm text-gray-800 outline-none shadow-sm focus:border-gray-300"
              />
            </div>
            <Select value={fCountry} onChange={setFCountry} options={facets.countries || []} placeholder="All countries" primary={primary} />
            <Select value={fState} onChange={setFState} options={facets.states || []} placeholder="All states" primary={primary} />
            <Select value={fCity} onChange={setFCity} options={facets.cities || []} placeholder="All cities" primary={primary} />
            <Select
              value={fRole}
              onChange={setFRole}
              options={[
                { value: "admin", label: plural(roleCounts.admins, "Admin") },
                { value: "instructor", label: plural(roleCounts.instructors, "Instructor") },
                { value: "member", label: plural(roleCounts.members, "Member") },
              ]}
              placeholder="All roles"
              primary={primary}
            />

            <button
              type="button"
              onClick={() => hasMyLocation && setNear((v) => !v)}
              disabled={!hasMyLocation}
              title={hasMyLocation ? "" : "Set your location in your profile to use this"}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold border shadow-sm transition disabled:opacity-50"
              style={{
                borderColor: near ? primary : "rgb(var(--c-gray-200))",
                backgroundColor: near ? primary : "rgb(var(--c-surface))",
                color: near ? "#fff" : "rgb(var(--c-gray-700))",
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 11a3 3 0 100-6 3 3 0 000 6z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s7-7.5 7-13a7 7 0 10-14 0c0 5.5 7 13 7 13z" />
              </svg>
              Near me
            </button>

            {activeFilters && (
              <button type="button" onClick={resetFilters} className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 ml-0.5">
                Clear
              </button>
            )}
          </div>

          {/* Legend — what each pin colour means, and how many of each are on the
              map right now. Each chip is also a shortcut to that role filter. */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {[
              { key: "admin", label: plural(roleCounts.admins, "Admin"), count: roleCounts.admins, tint: ROLE_TINT.Admin },
              { key: "instructor", label: plural(roleCounts.instructors, "Instructor"), count: roleCounts.instructors, tint: ROLE_TINT.Instructor },
              { key: "member", label: plural(roleCounts.members, "Member"), count: roleCounts.members, tint: primary },
            ].map((r) => {
              const on = fRole === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setFRole(on ? "" : r.key)}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition"
                  style={{
                    borderColor: on ? r.tint : "rgb(var(--c-gray-200))",
                    backgroundColor: on ? `${r.tint}14` : "rgb(var(--c-surface))",
                    color: on ? r.tint : "rgb(var(--c-gray-600))",
                  }}
                >
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-surface" style={{ backgroundColor: r.tint, boxShadow: `0 0 0 1px ${r.tint}55` }} />
                  {r.label}
                  <span className="text-gray-400">{r.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Big map */}
        <div className="relative">
          {loading ? (
            <MapSkeleton />
          ) : (
            <div style={{ height: 560, isolation: "isolate" }} className="max-md:h-[400px] relative z-0">
              <style>{MAP_STYLES}</style>
              <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom attributionControl={false} zoomControl={false} className="ff-map h-full w-full" style={{ height: "100%", width: "100%" }}>
                <ZoomControl position="bottomright" />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitTo points={plotted.map((m) => ({ lat: m._lat, lng: m._lng }))} focus={selected} />
                {plotted.map((m) => (
                  <Marker
                    key={m.community_member_id}
                    position={[m._lat, m._lng]}
                    icon={avatarIcon(m, primary, { active: selected?.community_member_id === m.community_member_id })}
                    zIndexOffset={isStaff(m) ? 400 : 0}
                    riseOnHover
                    eventHandlers={{ click: () => setSelected(m) }}
                  >
                    <Popup>
                      <MarkerCard m={m} primary={primary} onView={() => openDetail(m)} />
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              {/* Branded overlay — replaces the default Leaflet/OSM attribution
                  badge at the bottom-right. Uses the app favicon as the icon. */}
              {/* <div className="absolute bottom-3 right-3 z-[1000] flex items-center gap-1.5 rounded-full bg-surface/90 backdrop-blur px-3 py-1.5 shadow-md ring-1 ring-black/5">
                <img
                  src={asset("/favicon.ico")}
                  alt=""
                  className="w-4 h-4 rounded-sm object-contain"
                />
                <span className="text-[11px] font-semibold text-gray-700 leading-none">Flexicommunity Map</span>
              </div> */}

              {/* Empty state overlay */}
              {filtered.length === 0 && (
                <div className="absolute inset-0 z-[900] flex items-center justify-center bg-surface/55 backdrop-blur-[1px] p-6">
                  <div className="text-center bg-surface rounded-2xl shadow-xl ring-1 ring-black/5 px-6 py-5 max-w-xs">
                    <span className="mx-auto mb-3 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${primary}14`, color: primary }}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                        <circle cx="12" cy="11" r="2.5" />
                      </svg>
                    </span>
                    <p className="text-sm text-gray-600 font-medium">
                      {all.length === 0 ? "No members have shared a location yet." : "No members match these filters."}
                    </p>
                    {activeFilters && all.length > 0 && (
                      <button type="button" onClick={resetFilters} className="mt-3 text-sm font-semibold underline underline-offset-2" style={{ color: primary }}>
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin insights (region distribution) */}
      {isAdmin && distribution && (
        <DistributionPanel distribution={distribution} primary={primary} total={all.length} />
      )}

      {detail && <MemberDetailModal m={detail} primary={primary} onClose={() => setDetail(null)} />}
    </div>
  );

  // With a group card (passed from the group page) the map sits in the left
  // column and the group banner + My Points take the right rail, matching the
  // other pages.
  return (groupCard || pointsCard) ? (
    <div className="tpl-rail-grid">
      <aside className=" space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
        {groupCard}
        {pointsCard}
        <InlineBanner token={token} groupToken={groupToken} placement="map" wrapperClass="space-y-3" />
      </aside>
      <div className=" order-1">{topBanner}{mapBody}</div>
    </div>
  ) : mapBody;
}

function MarkerCard({ m, primary, onView }) {
  const role = roleOf(m);
  const tint = ROLE_TINT[role] || primary;

  return (
    <div className="min-w-[210px]">
      <div className="flex items-center gap-2.5">
        <SmallAvatar name={m.name} img={m.img} primary={primary} size={44} ring={isStaff(m) ? tint : undefined} />
        <div className="min-w-0">
          <p className="font-bold text-gray-800 text-sm leading-tight truncate" style={{ margin: 0 }}>{m.name}</p>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-[1px] text-[10px] font-bold uppercase tracking-wide"
            style={{ marginTop: 3, background: `${tint}1f`, color: tint }}
          >
            {role}
          </span>
          {m.location && <p className="text-[11px] text-gray-400 truncate" style={{ margin: "3px 0 0" }}>{m.location}</p>}
        </div>
      </div>
      <p className="text-xs text-gray-600 mt-2 line-clamp-3" style={{ margin: "8px 0 0" }}>{m.about || DEFAULT_ABOUT}</p>
      {m.distance_km != null && <p className="text-[11px] text-gray-400 mt-1" style={{ margin: "4px 0 0" }}>{m.distance_km} km away</p>}
      <button
        type="button"
        onClick={onView}
        className="mt-2.5 w-full text-white text-xs font-semibold py-1.5 rounded-lg hover:opacity-90"
        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
      >
        View Profile
      </button>
    </div>
  );
}

function MemberDetailModal({ m, primary, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-ink/60 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="bg-surface w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: `linear-gradient(135deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)` }}>
          <button onClick={onClose} aria-label="Close" className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white text-lg leading-none transition">
            ×
          </button>
          <div className="w-20 h-20 mx-auto rounded-2xl bg-surface shadow-md overflow-hidden flex items-center justify-center text-2xl font-bold" style={{ color: primary }}>
            {m.img ? <img src={m.img} alt="" className="w-full h-full object-cover" /> : (m.name || "?").charAt(0).toUpperCase()}
          </div>
          <h3 className="text-white text-xl font-bold mt-4 flex items-center justify-center gap-2">
            {m.name}
            {m.is_admin && <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-white/20">Admin</span>}
          </h3>
          {m.location && <p className="text-white/85 text-sm mt-1">{m.location}</p>}
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-gray-50 rounded-xl py-3">
              <div className="font-bold text-xl" style={{ color: primary }}>{m.points ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">Points</div>
            </div>
            <div className="bg-gray-50 rounded-xl py-3">
              <div className="font-bold text-sm text-gray-900">{m.distance_km != null ? `${m.distance_km} km` : "—"}</div>
              <div className="text-xs text-gray-500 mt-0.5">Distance</div>
            </div>
          </div>

          <div>
            <span className="text-[13px] font-semibold text-gray-600 mb-1 block">About</span>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{m.about || DEFAULT_ABOUT}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DistributionPanel({ distribution, primary, total }) {
  const byCountry = Object.entries(distribution.by_country || {});
  const max = byCountry.reduce((mx, [, c]) => Math.max(mx, c), 0) || 1;
  return (
    <div className="bg-surface rounded-3xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${primary}14`, color: primary }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 16l4-4 3 3 5-6" />
            </svg>
          </span>
          <div>
            <h4 className="font-bold text-gray-800 text-sm leading-tight">Member Distribution</h4>
            <p className="text-xs text-gray-400">Where your members are · admin view</p>
          </div>
        </div>
        <span className="shrink-0 text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: `${primary}14`, color: primary }}>
          {total} on map
        </span>
      </div>
      {byCountry.length === 0 ? (
        <EmptyState
          compact
          art="map"
          accent={primary}
          title="No locations yet"
          message="Members appear on the map once they add a city to their profile."
          padding="py-7"
        />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {byCountry.slice(0, 10).map(([country, count]) => (
            <li key={country}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-700 font-medium truncate">{country}</span>
                <span className="text-gray-400">{count}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(count / max) * 100}%`, background: `linear-gradient(90deg, ${primary} 0%, var(--brand-secondary, ${primary}aa) 100%)` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
